import * as React from 'react';
import { useCallback, useRef } from 'react';

import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';

import SearchOverlay from '../../../../base/components/SearchOverlay';
import CartSummaryAccessibilityLogger from '../../../../cart/components/atoms/CartSummaryAccessibilityLogger';
import A11yAnnouncer from '../../../../common/components/A11yAnnouncer/A11yAnnouncer';
import { useEcommerceSearchOverlayGA4ActionTracking } from '../../../../common/tracking/hooks/useSearchOverlayGA4Tracking';
import { useProductSearchStateContext } from '../../../../search/hooks/useProductSearchStateContext';
import useReactRouting from '../../../hooks/useReactRouting';
import CheckoutSearchOverlayResults from '../CheckoutSearchOverlay/CheckoutSearchOverlayResults';
import { buildAllProductResultsUrl } from '../EcommerceSearchBar.utils';

export const EcommerceSearchOverlayResults = React.lazy(
    () => import('./EcommerceSearchOverlayResults'),
);

const EcommerceSearchOverlay = (props: {
    type: 'checkout' | 'ecommerce';
    triggerRef?: React.RefObject<HTMLInputElement | null>;
}) => {
    const {
        query,
        queryUsed,
        isLoading,
        clear,
        result,
        searchFromPage,
        a11yResultAnnounceMessage,
        setA11yResultAnnounceMessage,
    } = useProductSearchStateContext();
    const { push } = useReactRouting();
    const searchRef = useRef<HTMLInputElement>(null);
    const isSearchFromCheckout = props.type === 'checkout';

    const {
        trackAbortAction,
        trackCloseClickAction,
        trackAutocompleteItemClick,
        trackListObjectClick,
        trackEnterAction,
        trackSeeAllResultsClickAction,
        trackAddToCartClickAction,
        trackRemoveFromCartClickAction,
    } = useEcommerceSearchOverlayGA4ActionTracking(
        query,
        result?.queryUsed,
        searchFromPage,
        isLoading,
    );

    const handleOutsideClick = () => {
        props.triggerRef?.current?.focus();
        trackAbortAction(result?.totalResults);
    };

    const handleClearClick = () => {
        trackCloseClickAction(result?.totalResults);
    };

    const handleOnHintClick = () => {
        trackAutocompleteItemClick(result?.totalResults);
        searchRef.current?.focus();
    };

    const handleDidYouMeanClick = () => {
        searchRef.current?.focus();
    };

    const handleItemClick = (item: ApiProduct, index: number) => {
        trackListObjectClick(result?.totalResults, item.identifier, item.name, index);
    };

    const handleItemAddToCartClick = (item: ApiProduct, index: number) => {
        trackAddToCartClickAction(result?.totalResults, item.identifier, item.name, index);
    };

    const handleItemRemoveFromCartClick = (item: ApiProduct, index: number) => {
        trackRemoveFromCartClickAction(result?.totalResults, item.identifier, item.name, index);
    };

    const usedQuery = isLoading ? query : queryUsed;

    const [isNavigating, setIsNavigating] = React.useState(false);

    const navigateToResultsPage = (_usedQuery?: string, _originalQuery?: string) => {
        if (_usedQuery) {
            const viewAllResultsUrl = buildAllProductResultsUrl(_usedQuery, _originalQuery);
            setIsNavigating(true);
            push(viewAllResultsUrl);
            clear();
        }
    };

    const handleCheckoutEnterPress = () => {
        // reannounce on enter to make sure screen readers read the message again
        setA11yResultAnnounceMessage('');
        setTimeout(() => {
            if (!isLoading) {
                setA11yResultAnnounceMessage(a11yResultAnnounceMessage);
            }
        }, 50);
    };

    const handleOnEnterPress = () => {
        if (isSearchFromCheckout) {
            handleCheckoutEnterPress();
        } else {
            trackEnterAction(result?.totalResults);
            navigateToResultsPage(usedQuery, query);
            searchRef.current?.blur();
        }
    };

    const handleFullResultsClick = () => {
        trackSeeAllResultsClickAction(result?.totalResults);
        navigateToResultsPage(usedQuery, query);
    };

    const onResultScroll = useCallback(() => searchRef.current?.blur(), [searchRef]);

    return (
        <SearchOverlay
            submitLabel="Sök vara"
            onEnterPress={handleOnEnterPress}
            onOutsideClick={handleOutsideClick}
            onClearClick={handleClearClick}
            ref={searchRef}
            inputPlaceholder="Sök bland tusentals varor"
            inputAriaLabel="Sökord"
            announcerSlot={
                <A11yAnnouncer
                    text={a11yResultAnnounceMessage}
                    announcerName="EcommerceSearchResultsAnnouncer"
                />
            }
            afterClose={() => {
                if (!isNavigating) {
                    props.triggerRef?.current?.focus();
                }
                setIsNavigating(false);
            }}
            dangerouslyDisableFocusBackToTrigger
        >
            {isSearchFromCheckout ? (
                <CheckoutSearchOverlayResults
                    onScroll={onResultScroll}
                    onFullResultsClick={handleFullResultsClick}
                    onHintClick={handleOnHintClick}
                    onDidYouMeanClick={handleDidYouMeanClick}
                    onItemAddToCartClick={handleItemAddToCartClick}
                    onItemRemoveFromCartClick={handleItemRemoveFromCartClick}
                    useMaxHeightCalculation
                />
            ) : (
                <EcommerceSearchOverlayResults
                    onScroll={onResultScroll}
                    onFullResultsClick={handleFullResultsClick}
                    onHintClick={handleOnHintClick}
                    onItemClick={handleItemClick}
                    onItemAddToCartClick={handleItemAddToCartClick}
                    onItemRemoveFromCartClick={handleItemRemoveFromCartClick}
                    useMaxHeightCalculation
                />
            )}

            <CartSummaryAccessibilityLogger announceOnChange />
        </SearchOverlay>
    );
};

export default EcommerceSearchOverlay;
